import React, { useMemo, useState } from "react";
import {
  useAssignContactsToAssetMutation,
  useAssignGroupsToAssetMutation,
  useGetContactsByAssetQuery,
  useGetGroupsByAssetQuery,
  useRemoveAllContactsFromAssetMutation,
  useRemoveContactsFromAssetMutation,
  useRemoveGroupsFromAssetMutation,
} from "../store/slices/mapsSlice";
import ContactsList from "../Contacts/ContactsList";
import { toCamel } from "../Maps/helpers";
import Contacts from "../Contacts/Contacts";
import { toast } from "react-toastify";
import MyModal from "../Common/MyModal";
import ContactsSimpleSearch from "../Contacts/ContactsSimpleSearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import ComingSoon from "../Monitoring/ComingSoon";
import NewFeature from "../Builder/Components/NewFeature";
import { useGetContactSetsQuery } from "../store/slices/contactsSlice";
import MyPagination from "../Common/MyPagination";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";
import ContactSetMembers from "../Contacts/ContactSetMembers";
import ContactSetsList from "../Contacts/ContactSetsList";
import ContactSetSearch from "../Contacts/ContactSetSearch";

const emptyArray: any[] = [];

export default function AssetContacts({
  selectedAsset,
}: {
  selectedAsset: any;
}) {
  const confirmDialog = useConfirmDialog();
  const [assetContactsPagination, setAssetContactsPagination] = useState({
    page: 0,
    perPage: 12,
  });
  const { data: contactsData } = useGetContactsByAssetQuery(
    { assetUuid: selectedAsset?.Uuid || "", ...assetContactsPagination },
    {
      skip: !selectedAsset?.Uuid,
    }
  );

  const [assetGroupsPagination, setAssetGroupsPagination] = useState({
    page: 0,
    perPage: 12,
  });
  const { data: assetGroupsData } = useGetGroupsByAssetQuery(
    { assetUuid: selectedAsset?.Uuid || "", ...assetGroupsPagination },
    {
      skip: !selectedAsset?.Uuid,
    }
  );

  const [allGroupsPagination, setAllGroupsPagination] = useState({
    page: 0,
    perPage: 12,
  });
  const { data: allGroups } = useGetContactSetsQuery(allGroupsPagination);
  const [csSearch, setCsSearch] = useState("");
  const [csType, setCsType] = useState("");
  const [orderBy, setOrderBy] = useState<string | undefined>(undefined);

  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [tobeRemoved, setTobeRemoved] = useState([]);
  const [replace, setReplace] = useState(false);

  const [save] = useAssignContactsToAssetMutation();
  const [saveGroups] = useAssignGroupsToAssetMutation();

  const [contactModalReset, setContactModalReset] = useState(Date.now());
  const handleSave = async (isClear = false) => {
    if (
      isClear &&
      !(await confirmDialog(
        "Clear contacts",
        `Are you sure you want to clear all contacts from this asset?`
      ))
    ) {
      return;
    }
    const data: any = await save({
      contactUuids: isClear ? [] : selectedContacts,
      assetUuid: selectedAsset?.Uuid || "",
      isDelete: isClear ? true : replace,
    });

    if (data.isError) {
      toast.error(data.message);
      return;
    }
    if (data) {
      toast.success("Contacts assigned successfully");
      setContactModalReset(Date.now());
      setSelectedContacts([]);
    }
  };

  const handleSaveGroups = async (isClear = false) => {
    if (
      isClear &&
      !(await confirmDialog(
        "Clear groups",
        `Are you sure you want to clear all groups from this asset?`
      ))
    ) {
      return;
    }
    const data: any = await saveGroups({
      groupUuids: isClear ? [] : selectedContacts,
      assetUuid: selectedAsset?.Uuid || "",
      isDelete: isClear ? true : replace,
    });

    if (data.isError) {
      toast.error(data.message);
      return;
    }
    if (data) {
      toast.success("Groups assigned successfully");
      setContactModalReset(Date.now());
      setSelectedContacts([]);
    }
  };

  const [filter, setFilter] = useState([
    // Word search
    {
      values: [""],
      is_or: true,
      fields: [],
      formats: ["card", "primary"],
      order: 0,
      operator: "prefix",
    },
    // Status filter
    {
      fields: ["status"],
      formats: [],
      isOr: false,
      offset: "0",
      offsetFrom: "fixed",
      offsetType: "positive",
      offsetUnit: "day",
      operator: "is",
      order: 0,
      thread: "CurrentThread",
      timeZone: "",
      timeZoneFrom: "fixed",
      valueId: "",
      values: ["active"],
      valueType: "fixed",
    },
  ]);

  const [removeContacts] = useRemoveContactsFromAssetMutation();
  const [removeAllContacts] = useRemoveAllContactsFromAssetMutation();
  const contactsUuids = useMemo(
    () => contactsData?.contacts?.map((i) => i.uuid) || [],
    [contactsData]
  );

  const [removeGroups] = useRemoveGroupsFromAssetMutation();

  return (
    <div className="p-3">
      <div>
        <div>
        <span className="fw-bold"> Asset label:</span>
          <span className="text-secondary ms-2">{selectedAsset?.Label}</span>
        </div>
        <div>
          <span className="fw-bold"> Asset description:</span>
          <span className="text-secondary ms-2">
            {selectedAsset?.Description}
          </span>
        </div>
        <div className="text-secondary">
          Assign any contacts or groups associated to the Asset
        </div>
      </div>
      <div className="bg-purple-gradient-darker-reverse rounded-4 p-3 mt-4 mb-2 ">
        <div className="d-flex align-items-center mb-3">
          <div className="fw-bold fs-5">
            Contacts currently assigned to the asset
          </div>
          <div className="flex-grow-1" />
          {tobeRemoved.length > 0 ? (
            <button
              className="btn btn-colored p-2 rounded-3"
              onClick={() =>
                removeContacts({
                  assetUuid: selectedAsset?.Uuid || "",
                  contactUuids: tobeRemoved,
                }).then((data: any) => {
                  if (data.error) {
                    toast.error("Error removing contacts");
                    return;
                  }
                  toast.success("Contacts removed successfully");
                  setTobeRemoved([]);
                })
              }
            >
              Remove selected contacts
            </button>
          ) : (
            ""
          )}
          <MyModal
            label="Assign Contacts"
            heading="Assign contacts to asset"
            buttonClassName="btn btn-colored p-2"
            size="xxl"
            key={contactModalReset}
          >
            <div style={{ minHeight: 720 }}>
              <div className="bg-colored-reverse p-3 rounded-3 d-flex flex-wrap align-items-end mb-3 gap-2">
                <ContactsSimpleSearch filter={filter} setFilter={setFilter} />
              </div>
              <Contacts
                filters={filter}
                fields={undefined}
                useSearchOptions={true}
                includeContacts={undefined}
                ignoreContacts={contactsUuids}
                ignoreSets={undefined}
                contactSet={undefined}
                contactSets={emptyArray}
                strictCriteria={undefined}
                doNotUseSets={undefined}
                contactButton={undefined}
                notDraggable={undefined}
                onDrop={undefined}
                allContactsButton={undefined}
                isModal={undefined}
                showContactFields={undefined}
                setSelectedContacts={setSelectedContacts}
              />
            </div>
            <div className="d-flex flex-row justify-content-end align-items-center mb-3">
              <div className="form-check me-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="replace"
                  checked={replace}
                  onChange={(e) => setReplace(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="replace">
                  Replace existing contacts
                </label>
              </div>
              <button
                className="btn btn-colored"
                onClick={() => handleSave(false)}
                disabled={selectedContacts.length === 0}
              >
                Save
              </button>
            </div>
          </MyModal>
        </div>
      </div>

      {contactsData?.contacts ? (
        <>
          <ContactsList
            contacts={contactsData.contacts.map((i) => toCamel(i))}
            refreshList={undefined}
            notDraggable={undefined}
            fullView={undefined}
            isModal={undefined}
            showContactFields={undefined}
            onSelectContacts={undefined}
            contactButton={(c: any) => (
              <button
                className="btn btn-outline-danger border-0 rounded-3"
                title="Remove contact"
                onClick={() => {
                  removeContacts({
                    assetUuid: selectedAsset?.Uuid || "",
                    contactUuids: [c.uuid],
                  }).then((data: any) => {
                    if (data.error) {
                      toast.error("Error removing contact");
                      return;
                    }
                    toast.success("Contact removed successfully");
                  });
                }}
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
            )}
            allContactsButton={() => (
              <button
                className="btn btn-outline-danger border-0 rounded-3"
                title="Remove all contacts"
                onClick={() => {
                  removeAllContacts({
                    assetUuid: selectedAsset?.Uuid || "",
                  }).then((data: any) => {
                    if (data.error) {
                      toast.error("Error removing all contacts");
                      return;
                    }
                    toast.success("All contacts removed successfully");
                  });
                }}
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
            )}
          />
          <MyPagination
            total={contactsData?.total || 0}
            onPaginate={(page: number, perPage: number) => {
              setAssetContactsPagination({ page, perPage });
            }}
          />
        </>
      ) : (
        <div className="alert alert-secondary">
          No contacts assigned to this asset.
        </div>
      )}

      <div className="bg-purple-gradient-darker-reverse rounded-4 p-3 mt-5 mb-2 ">
        <div className="d-flex align-items-center mb-3">
          <div className="fw-bold fs-5">
            Groups currently assigned to the asset
          </div>
          <div className="flex-grow-1" />
          {tobeRemoved.length > 0 ? (
            <button
              className="btn btn-colored p-2 rounded-3"
              onClick={() =>
                removeContacts({
                  assetUuid: selectedAsset?.Uuid || "",
                  contactUuids: tobeRemoved,
                }).then((data: any) => {
                  if (data.error) {
                    toast.error("Error removing groups");
                    return;
                  }
                  toast.success("Groups removed successfully");
                  setTobeRemoved([]);
                })
              }
            >
              Remove selected groups
            </button>
          ) : (
            ""
          )}

          <MyModal
            label="Assign Groups"
            heading="Assign groups to asset"
            buttonClassName="btn btn-colored p-2"
            size="xxl"
            key={contactModalReset}
          >
            <div style={{ minHeight: 720 }}>
              {allGroups && allGroups.contactSets?.length > 0 ? (
                <>
                  <div className="bg-colored-reverse p-3 rounded-3 d-flex flex-wrap align-items-end mb-3 gap-2">
                    <ContactSetSearch
                      search={csSearch}
                      setSearch={setCsSearch}
                      csType={csType}
                      setCsType={setCsType}
                      orderBy={orderBy}
                      setOrderBy={setOrderBy}
                    />
                  </div>
                  <ContactSetsList
                    contactSetButton={(cs: any) =>
                      selectedContacts.includes(cs.uuid) ? (
                        <button
                          type="button"
                          className="btn btn-outline-danger border-0 btn-sm "
                          onClick={() => {
                            setSelectedContacts?.((prev) => {
                              const newSelected = [
                                ...prev.filter((cuuid) => cuuid !== cs.uuid),
                              ];
                              return newSelected;
                            });
                          }}
                        >
                          <FontAwesomeIcon icon={faMinus} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-outline-primary border-0 btn-sm "
                          onClick={() => {
                            setSelectedContacts?.((prev) => [...prev, cs.uuid]);
                          }}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      )
                    }
                    search={csSearch}
                    csType={csType}
                    orderBy={orderBy}
                    refresh={undefined}
                    isModal={undefined}
                    fullView={false}
                    excludeUuids={assetGroupsData?.groups?.map(
                      (g: any) => g.uuid
                    )}
                  />
                </>
              ) : (
                <div>No groups available. Create groups to assign them.</div>
              )}
              {/* <MyPagination
                total={allGroups?.pagination?.total}
                onPaginate={(page: number, perPage: number) => {
                  setAllGroupsPagination({ page, perPage });
                }}
              /> */}
            </div>
            <div className="d-flex flex-row justify-content-end align-items-center mb-3">
              <div className="form-check me-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="replace"
                  checked={replace}
                  onChange={(e) => setReplace(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="replace">
                  Replace existing contacts
                </label>
              </div>
              <button
                className="btn btn-colored"
                onClick={() => handleSaveGroups(false)}
                disabled={selectedContacts.length === 0}
              >
                Save
              </button>
            </div>
          </MyModal>
        </div>
      </div>
      {!!assetGroupsData?.groups?.length ? (
        <table className="table">
          <thead>
            <tr>
              <th></th>
              <th>Group name </th>
              <th>Type</th>
              <th>Members count</th>
            </tr>
          </thead>
          <tbody>
            {assetGroupsData &&
              assetGroupsData.groups?.map((g: any) => (
                <tr key={g.uuid}>
                  <td>
                    <button
                      className="btn btn-outline-danger border-0 rounded-3"
                      title="Remove group"
                      aria-label={`Remove group ${g.label || g.uuid}`}
                      onClick={() => {
                        removeGroups({
                          assetUuid: selectedAsset?.Uuid || "",
                          groupUuids: [g.uuid],
                        }).then((data: any) => {
                          if (data.error) {
                            toast.error("Error removing group");
                            return;
                          }
                          toast.success("Group removed successfully");
                        });
                      }}
                    >
                      <FontAwesomeIcon icon={faMinus} aria-hidden="true" />
                    </button>
                  </td>
                  <td>{g.label}</td>
                  <td>{g.type}</td>
                  <td>
                    {g.members_count ? (
                      <ContactSetMembers
                        uuid={g.uuid}
                        label={g.members_count + " members"}
                        contactButton={undefined}
                      />
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      ) : (
        <div className="alert alert-secondary">
          No groups assigned to this asset.
        </div>
      )}
      <MyPagination
        total={assetGroupsData?.total}
        onPaginate={(page: number, perPage: number) => {
          setAssetGroupsPagination({ page, perPage });
        }}
      />
    </div>
  );
}
